import React from 'react';
import styled from 'styled-components';
import { ReactComponent as UnexpectedErrorIllustration } from '../../assets/svg/unexpected-error-illustration.svg';
import { PRIVATE_ROUTE } from '../../utils/constants';

type Props = {
  onTryAgain?: () => void;
  onBackToHome?: () => void;
};

const UnexpectedErrorPage = ({ onTryAgain, onBackToHome }: Props) => {
  const handleTryAgain = () => {
    if (onTryAgain) {
      onTryAgain();
      return;
    }
    window.location.reload();
  };

  const handleBackToHome = () => {
    if (onBackToHome) {
      onBackToHome();
      return;
    }
    window.location.assign(PRIVATE_ROUTE.campaigns);
  };

  return (
    <Page>
      <Content>
        <Illustration aria-hidden>
          <UnexpectedErrorIllustration />
        </Illustration>
        <Title>Oops, that&apos;s our bad</Title>
        <Description>
          Something went wrong on our end. Try again, or head back home — if it keeps happening, please contact support.
        </Description>
        <Actions>
          <SecondaryButton type="button" onClick={handleTryAgain}>
            Try Again
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleBackToHome}>
            Back to Home
          </PrimaryButton>
        </Actions>
      </Content>
    </Page>
  );
};

export default UnexpectedErrorPage;

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: calc(var(--app-vh, 1vh) * 100);
  padding: 24px;
  background: #ffffff;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 520px;
  text-align: center;
`;

const Illustration = styled.div`
  margin-bottom: 32px;

  svg {
    display: block;
    width: 200px;
    height: auto;
  }
`;

const Title = styled.h1`
  margin: 0 0 16px;
  color: #000000;
  font-family: 'Inter', 'Helvetica Neue', sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
`;

const Description = styled.p`
  margin: 0 0 32px;
  color: #717171;
  font-family: 'Inter', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const buttonStyles = `
  min-width: 148px;
  height: 44px;
  padding: 0 20px;
  border-radius: 8px;
  font-family: 'Inter', 'Helvetica Neue', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.92;
  }
`;

const SecondaryButton = styled.button`
  ${buttonStyles}
  border: 1px solid #d9d9d9;
  background: #ffffff;
  color: #000000;
`;

const PrimaryButton = styled.button`
  ${buttonStyles}
  border: 0;
  background: #5770c6;
  color: #ffffff;
`;
